/**
 * Essential Data Formatters for PDF Generation
 * 
 * Only the formatting functions actually needed for @react-pdf/renderer.
 */

import { createT } from './simpleTranslate';

/**
 * Format date for PDF display
 * @param date - Date string or Date object
 * @param locale - Locale for formatting (default: 'fr')
 * @returns Formatted date string
 */
export const formatDateForPDF = (date: string | Date, locale: string = 'fr'): string => {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) {
      const t = createT(locale);
      return t('general.invalid_date', 'Date invalide');
    }
    
    return dateObj.toLocaleDateString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return String(date);
  }
};

/**
 * Format patient full name
 * @param personalData - Patient personal data object
 * @returns Formatted full name
 */
export const formatPatientName = (personalData: {
  name?: string;
  surnames?: string;
}): string => {
  const { name = '', surnames = '' } = personalData;
  return `${name} ${surnames}`.trim();
};

/**
 * Format patient ID for display
 * @param patient - Patient object with various ID fields
 * @returns Formatted patient ID
 */
export const formatPatientID = (patient: {
  personalData?: {
    healthId?: string;
    nationalId?: string;
    passportNumber?: string;
  };
}): string => {
  const { personalData } = patient;
  if (!personalData) return 'N/A';
  
  return personalData.healthId || 
         personalData.nationalId || 
         personalData.passportNumber || 
         'N/A';
};